/**
 * Operations — real page. Auth-gated container with 3 tabs.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Upload, ClipboardList, LayoutDashboard, LogOut, User, HelpCircle, Landmark, Building,
} from "lucide-react";
import { toast } from "sonner";
import {
  getConfig, getToken, clearToken, listTransactions,
} from "@/lib/operations/api";
import AuthGate from "@/pages/operations/AuthGate";
import UploadPanel from "@/pages/operations/UploadPanel";
import ReviewPanel from "@/pages/operations/ReviewPanel";
import DashboardPanel from "@/pages/operations/DashboardPanel";
import PersonalPanel from "@/pages/operations/PersonalPanel";
import UnknownPanel from "@/pages/operations/UnknownPanel";
import LoansPanel from "@/pages/operations/LoansPanel";

export default function Operations() {
  const [authed, setAuthed] = useState(() => !!getToken());
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("review");
  const [refreshKey, setRefreshKey] = useState(0);
  // When set, the Review tab is filtered to a single statement.
  const [reviewStatement, setReviewStatement] = useState(null);
  // Live counts for the attention-worthy tabs. When zero, the tab is
  // hidden so the bar stays clean for fresh setups but always available
  // once a matching row exists.
  const [personalCount, setPersonalCount] = useState(0);
  const [unknownCount, setUnknownCount] = useState(0);
  const [loansCount, setLoansCount] = useState(0);
  // Global sponsor filter. Threaded down to every panel so switching a
  // sponsor persists across Upload / Review / Personal / Unknown / Loans
  // without each panel keeping its own copy. "" == All.
  const [sponsor, setSponsor] = useState("");
  // Lifted from ReviewPanel so the Pending-only checkbox can live in
  // the header next to Sign out for a cleaner toolbar in the Review tab.
  const [onlyPending, setOnlyPending] = useState(true);
  const SPONSORS = ["420", "HepCURE", "Christian", "Joséphine", "George", "Racquel"];

  const loadConfig = useCallback(async () => {
    try { setConfig(await getConfig()); }
    catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearToken();
        setAuthed(false);
      } else {
        toast.error("Failed to load config");
      }
    }
  }, []);

  useEffect(() => { if (authed) loadConfig(); }, [authed, loadConfig]);

  // Refresh the attention counts whenever the auth state changes or any
  // panel bumps `refreshKey` after an edit/delete.
  useEffect(() => {
    if (!authed) return;
    Promise.all([
      listTransactions({ category: "personal" }).catch(() => []),
      listTransactions({ category: "unknown" }).catch(() => []),
      listTransactions({ is_loan: true }).catch(() => []),
    ]).then(([p, u, l]) => {
      setPersonalCount(p.length);
      setUnknownCount(u.length);
      setLoansCount(l.length);
    });
  }, [authed, refreshKey]);

  // If the current tab disappears (its count drops to 0), bounce to Review.
  useEffect(() => {
    if (tab === "personal" && personalCount === 0) setTab("review");
    if (tab === "unknown"  && unknownCount  === 0) setTab("review");
    if (tab === "loans"    && loansCount    === 0) setTab("review");
  }, [tab, personalCount, unknownCount, loansCount]);

  if (!authed) {
    return <AuthGate onAuthed={() => setAuthed(true)} />;
  }
  if (!config) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;
  }

  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16" data-testid="operations-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-teal">
              HepCURE Ops
            </p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold font-heading text-brand-navy">
              Expense Operations
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Upload bank statements, review each transaction, and see spend by cost centre.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {tab === "review" && (
              <label
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-brand-navy hover:bg-slate-100 font-semibold text-sm cursor-pointer transition"
                data-testid="ops-review-only-pending"
              >
                <input
                  type="checkbox"
                  checked={onlyPending}
                  onChange={(e) => setOnlyPending(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                Pending only
              </label>
            )}
            <button
              type="button"
              onClick={() => { clearToken(); setAuthed(false); toast.success("Signed out"); }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-brand-navy hover:bg-slate-100 font-semibold text-sm transition"
              data-testid="ops-signout"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-6 border-b border-slate-200">
          <nav className="flex gap-4 sm:gap-6 flex-nowrap overflow-x-auto overflow-y-hidden touch-pan-x -mx-1 px-1 hide-scrollbar">
            {[
              { key: "upload",    label: "Upload",    icon: Upload },
              { key: "review",    label: "Review",    icon: ClipboardList },
              { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
              // Attention tabs — only rendered when their bucket has at
              // least one row so the tab bar stays clean for fresh
              // setups. Grouped after Dashboard, next to Personal so the
              // three read as a triage cluster.
              ...(personalCount > 0
                ? [{ key: "personal", label: "Personal", icon: User,        badge: personalCount }]
                : []),
              ...(unknownCount > 0
                ? [{ key: "unknown",  label: "Unknown",  icon: HelpCircle,  badge: unknownCount }]
                : []),
              ...(loansCount > 0
                ? [{ key: "loans",    label: "Loans",    icon: Landmark,    badge: loansCount }]
                : []),
            ].map(({ key, label, icon: Icon, badge }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 pb-3 -mb-px border-b-2 text-sm font-semibold transition whitespace-nowrap shrink-0 ${
                  tab === key
                    ? "border-brand-teal text-brand-navy"
                    : "border-transparent text-slate-500 hover:text-brand-navy"
                }`}
                data-testid={`ops-tab-${key}`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {typeof badge === "number" && badge > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-brand-navy text-white text-[10px] font-bold tabular-nums"
                    data-testid={`ops-tab-${key}-badge`}
                  >
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Global sponsor row — sits directly below the tabs and looks
            identical (text with a teal underline for the active pick).
            Persists across every tab and filters each panel's data. */}
        <div className="mt-3 border-b border-slate-200" data-testid="ops-sponsor-row">
          <nav className="flex gap-4 sm:gap-6 flex-nowrap overflow-x-auto overflow-y-hidden touch-pan-x -mx-1 px-1 hide-scrollbar">
            {[{ key: "", label: "All" }, ...SPONSORS.map((s) => ({ key: s, label: s }))].map(({ key, label }) => {
              const active = sponsor === key;
              return (
                <button
                  key={key || "__all__"}
                  type="button"
                  onClick={() => setSponsor(key)}
                  className={`flex items-center gap-2 pb-3 -mb-px border-b-2 text-sm font-semibold transition whitespace-nowrap shrink-0 ${
                    active
                      ? "border-brand-teal text-brand-navy"
                      : "border-transparent text-slate-500 hover:text-brand-navy"
                  }`}
                  data-testid={`ops-sponsor-${key ? key.replace(/\s+/g, "-").toLowerCase() : "all"}`}
                >
                  <Building className="h-4 w-4" />
                  {label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {tab === "upload"    && (
          <UploadPanel
            onImported={bump}
            onOpenStatement={(s) => { setReviewStatement(s); setTab("review"); }}
            sponsor={sponsor}
          />
        )}
        {tab === "review"    && (
          <ReviewPanel
            config={config}
            onChange={bump}
            statement={reviewStatement}
            onClearStatement={() => setReviewStatement(null)}
            sponsor={sponsor}
            onlyPending={onlyPending}
            setOnlyPending={setOnlyPending}
          />
        )}
        {tab === "dashboard" && <DashboardPanel config={config} refreshKey={refreshKey} sponsor={sponsor} />}
        {tab === "personal"  && <PersonalPanel config={config} refreshKey={refreshKey} onChange={bump} sponsor={sponsor} />}
        {tab === "unknown"   && <UnknownPanel  config={config} refreshKey={refreshKey} onChange={bump} sponsor={sponsor} />}
        {tab === "loans"     && <LoansPanel    config={config} refreshKey={refreshKey} onChange={bump} sponsor={sponsor} />}
      </main>
    </div>
  );
}
